import { DeviceTag } from '../../types';
import { Measurement, MeasurementDimension } from '../../../query/query.types';

export const DEVICE_TAG_DIMENSION_PREFIX = 'i_device_tags ->> ';

/**
 * Returns the dimension key for a device tag.
 * Format: `i_device_tags:{tag.name}`
 */
export function getDeviceTagDimensionKey(tagName: string): string {
  return `${DEVICE_TAG_DIMENSION_PREFIX}'${tagName}'`;
}

/**
 * Extracts the tag name from a full device tag dimension key.
 * Example: `i_device_tags ->> 'color'` → `color`
 * Returns `undefined` if the string is not a valid device tag dimension key.
 */
export function getTagNameFromDimensionKey(dimensionKey: string): string | undefined {
  if (!dimensionKey.startsWith(DEVICE_TAG_DIMENSION_PREFIX)) {
    return undefined;
  }
  const raw = dimensionKey.slice(DEVICE_TAG_DIMENSION_PREFIX.length);
  // Strip surrounding single quotes if present
  if (raw.startsWith("'") && raw.endsWith("'")) {
    return raw.slice(1, -1);
  }
  return raw;
}

/**
 * Transforms an array of device tags into a map of dimension definitions.
 * Each tag becomes a dimension with key `i_device_tags:{tag.name}`.
 */
export function transformDeviceTagDimensions(tags: DeviceTag[]): Record<string, MeasurementDimension> {
  return tags.reduce<Record<string, MeasurementDimension>>((acc, tag) => {
    acc[getDeviceTagDimensionKey(tag.name)] = {
      Label: tag.display_name,
      Column: getDeviceTagDimensionKey(tag.name),
      Alias: tag.name,
      Type: 'string',
      Category: 'Device Tags'
    };
    return acc;
  }, {});
}

/**
 * Builds a map of tag display name → dimension key from an already-augmented measurements array.
 * Used to resolve guided variable names (e.g. "Color") to their filter field
 * (e.g. `i_device_tags ->> 'color'`) inside getGuidedDimensionOptions.
 * Since device tags are augmented uniformly across all measurements, checking the first is sufficient.
 */
export function getDeviceTagKeysByDisplayName(measurements: Measurement[]): Record<string, string> {
  if (!measurements.length) {
    return {};
  }
  const result: Record<string, string> = {};
  for (const [key, dim] of Object.entries(measurements[0].storage.Dimensions)) {
    if (key.startsWith(DEVICE_TAG_DIMENSION_PREFIX) && dim.Label) {
      result[dim.Label] = key;
    }
  }
  return result;
}

/**
 * Augments all traffic measurements with device tag dimensions.
 * Device tags are common dimensions available across all traffic measurements,
 * accessible via the `i_device_tags:` prefix.
 */
export function augmentMeasurementsWithDeviceTags(measurements: Measurement[], tags: DeviceTag[]): Measurement[] {
  if (!tags.length) {
    return measurements;
  }

  const tagDimensions = transformDeviceTagDimensions(tags);

  return measurements.map((measurement) => {
    return {
      ...measurement,
      storage: {
        ...measurement.storage,
        Dimensions: {
          ...measurement.storage.Dimensions,
          ...tagDimensions
        }
      }
    };
  });
}
